/* ============================================================================
   ColoredByte (CB) theme extensions — standalone script bundle
   ----------------------------------------------------------------------------
   Loaded from layout/theme.liquid ("CB extensions" block).
   This file is intentionally kept separate from the compiled theme JS so it
   can be reviewed and merged into app.js by the theme team later.

   Everything is exposed as custom elements. Each element owns a single
   behavior and reads its markup from the Liquid templates:

     <cb-cart-recycle-fee>  Recycling fee notice in the cart drawer
                            (markup: snippets/bag.liquid)
     <cb-pdp-recycle-fee>   Recycling fee notice on the product page
                            (markup: snippets/product-configurable.liquid)
     <cb-tout-arrows>       Desktop prev/next arrows for tout carousels
                            (mounted automatically into .js-tout-section)
     <cb-countdown-offset>  Keeps the countdown timer positioned below the
                            header (markup: snippets/countdown-timer.liquid)
   ========================================================================== */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     Shared helpers
     ------------------------------------------------------------------------ */

  function onDocumentReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  var CbMoney = {
    /* Cart API prices come in cents; delegate to the theme formatter if present. */
    fromCents: function (cents) {
      if (window._ && typeof window._.formatPriceDisplay === 'function') {
        return window._.formatPriceDisplay(cents);
      }
      return '$' + (Number(cents || 0) / 100).toFixed(2).replace(/\.00$/, '');
    },

    fromAmount: function (amount) {
      var value = Number(amount || 0);
      if (!value) return '';
      return '$' + value.toFixed(2).replace(/\.00$/, '');
    }
  };

  /* --------------------------------------------------------------------------
     Base behavior: tooltip disclosure
     ----------------------------------------------------------------------------
     Toggle button:  [data-cb-tooltip-toggle]
     Tooltip body:   [data-cb-tooltip]
     Open state is the `is-open` class on the host element; closes on outside
     click and Escape. Subclasses only implement what text to show and when.
     ------------------------------------------------------------------------ */

  class CbTooltipDisclosure extends HTMLElement {
    connectedCallback() {
      if (this.cbDisclosureBound) return;
      this.cbDisclosureBound = true;

      var self = this;

      this.addEventListener('click', function (event) {
        var toggle = event.target.closest('[data-cb-tooltip-toggle]');
        if (!toggle) return;

        event.preventDefault();
        event.stopPropagation();
        self.setOpen(!self.classList.contains('is-open'));
      });

      document.addEventListener('click', function (event) {
        if (!self.contains(event.target)) {
          self.setOpen(false);
        }
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          self.setOpen(false);
        }
      });
    }

    setOpen(isOpen) {
      this.classList.toggle('is-open', isOpen);

      var toggle = this.querySelector('[data-cb-tooltip-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    }
  }

  /* --------------------------------------------------------------------------
     <cb-cart-recycle-fee>
     ----------------------------------------------------------------------------
     Shows "$X Mattress Recycling Fee included" in the cart drawer whenever the
     recycle fee product (SDG.Data.recycleProductId) is in the cart.
     The cart updates asynchronously, so refreshes are retried a few times.
     ------------------------------------------------------------------------ */

  var CART_REFRESH_DELAYS_MS = [100, 500, 1200];
  var CART_REFRESH_TRIGGER_SELECTOR =
    '#bagBtn, #btnAddToBag, .js-increment-btn, .js-bag-item-remove';

  class CbCartRecycleFee extends CbTooltipDisclosure {
    connectedCallback() {
      super.connectedCallback();

      if (this.cbCartBound) return;
      this.cbCartBound = true;

      var self = this;

      this.scheduleRefresh();

      var bagItems = document.getElementById('bagItems');
      if (bagItems && window.MutationObserver) {
        new MutationObserver(function () {
          self.scheduleRefresh();
        }).observe(bagItems, { childList: true, subtree: true });
      }

      document.addEventListener('click', function (event) {
        if (event.target.closest(CART_REFRESH_TRIGGER_SELECTOR)) {
          self.scheduleRefresh();
        }
      });
    }

    scheduleRefresh() {
      var self = this;
      CART_REFRESH_DELAYS_MS.forEach(function (delay) {
        setTimeout(function () {
          self.refresh();
        }, delay);
      });
    }

    refresh() {
      if (!window.SDG || !SDG.Data || !SDG.Data.recycleProductId) return;

      var self = this;
      var recycleId = Number(SDG.Data.recycleProductId);

      fetch('/cart.js')
        .then(function (res) {
          return res.json();
        })
        .then(function (cart) {
          self.render(self.findRecycleLine(cart, recycleId));
        })
        .catch(function (error) {
          console.warn('[CB] Recycle fee notice failed', error);
        });
    }

    findRecycleLine(cart, recycleId) {
      return cart.items.find(function (item) {
        return Number(item.id) === recycleId || Number(item.variant_id) === recycleId;
      });
    }

    render(recycleLine) {
      if (!recycleLine) {
        this.hidden = true;
        this.setOpen(false);
        return;
      }

      var price = CbMoney.fromCents(
        recycleLine.final_line_price ||
        recycleLine.line_price ||
        recycleLine.original_line_price ||
        recycleLine.original_price * recycleLine.quantity
      );

      var text = this.querySelector('[data-cb-text]');
      if (text) {
        text.textContent = price + ' Mattress Recycling Fee included';
      }

      this.hidden = false;
    }
  }

  /* --------------------------------------------------------------------------
     <cb-pdp-recycle-fee>
     ----------------------------------------------------------------------------
     Shows the recycling fee notice on the product page when the selected
     variant carries a recycle fee (SDG.Data.recycleFee). Re-checks after
     variant changes; extra delayed checks cover async form updates.
     ------------------------------------------------------------------------ */

  var PDP_RECYCLE_FEE_PER_PIECE = 18;

  class CbPdpRecycleFee extends CbTooltipDisclosure {
    connectedCallback() {
      super.connectedCallback();

      if (this.cbPdpBound) return;
      this.cbPdpBound = true;

      var self = this;

      document.addEventListener('change', function (event) {
        if (event.target && event.target.name === 'id') {
          setTimeout(function () {
            self.update();
          }, 50);
        }
      });

      document.addEventListener('click', function () {
        setTimeout(function () {
          self.update();
        }, 100);
        setTimeout(function () {
          self.update();
        }, 400);
      });

      this.update();
      setTimeout(function () {
        self.update();
      }, 500);
      setTimeout(function () {
        self.update();
      }, 1200);
    }

    getSelectedVariantId() {
      var form = document.querySelector('form[action*="/cart/add"]');
      var input = form
        ? form.querySelector('[name="id"]')
        : document.querySelector('[name="id"]');

      return input ? Number(input.value) : null;
    }

    getRecycleFeeForVariant(variantId) {
      if (!window.SDG || !SDG.Data || !Array.isArray(SDG.Data.recycleFee)) return null;

      return SDG.Data.recycleFee.find(function (item) {
        return Number(item.id) === Number(variantId);
      });
    }

    update() {
      var recycleItem = this.getRecycleFeeForVariant(this.getSelectedVariantId());

      if (!recycleItem || !Number(recycleItem.recycleFee)) {
        this.hidden = true;
        this.setOpen(false);
        return;
      }

      var price = CbMoney.fromAmount(
        PDP_RECYCLE_FEE_PER_PIECE * Number(recycleItem.recycleFee || 1)
      );

      var text = this.querySelector('[data-cb-text]');
      if (text) {
        text.textContent = 'Mattress Recycling Fee will be added at cart';
      }

      var tooltip = this.querySelector('[data-cb-tooltip]');
      if (tooltip) {
        tooltip.innerHTML =
          'California state law requires retailers to collect a ' + price +
          ' recycling fee per piece on every mattress, foundation, box spring, and adjustable base sold. ' +
          'The amount was set by the state and is not controlled by Sit&apos;n Sleep. ' +
          'The fee funds the California Mattress Recycling Program operated by the Mattress Recycling Council.';
      }

      this.hidden = false;
    }
  }

  /* --------------------------------------------------------------------------
     <cb-tout-arrows>
     ----------------------------------------------------------------------------
     Renders desktop prev/next arrows for a tout carousel and navigates by
     clicking the matching Flickity page dot. Mounted by CbToutArrowsMounter
     because the carousels (and their dots) initialize asynchronously.
     ------------------------------------------------------------------------ */

  class CbToutArrows extends HTMLElement {
    connectedCallback() {
      if (this.cbArrowsBound) return;
      this.cbArrowsBound = true;

      this.appendChild(this.createArrow('prev'));
      this.appendChild(this.createArrow('next'));

      var self = this;
      this.addEventListener('click', function (event) {
        var arrow = event.target.closest('.cb-tout-arrow');
        if (arrow) {
          self.navigate(arrow.dataset.direction);
        }
      });
    }

    createArrow(direction) {
      var button = document.createElement('button');

      button.type = 'button';
      button.className = 'cb-tout-arrow cb-tout-arrow--' + direction;
      button.dataset.direction = direction;
      button.setAttribute('aria-label', direction === 'next' ? 'Next' : 'Previous');
      button.innerHTML = direction === 'next' ? '›' : '‹';

      return button;
    }

    navigate(direction) {
      var section = this.closest('.js-tout-section');
      if (!section) return;

      var dots = Array.prototype.slice.call(
        section.querySelectorAll('.flickity-page-dots li.dot')
      );
      if (!dots.length) return;

      var currentIndex = dots.findIndex(function (dot) {
        return dot.classList.contains('is-selected');
      });
      if (currentIndex === -1) return;

      var targetIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
      if (targetIndex >= dots.length) targetIndex = 0;
      if (targetIndex < 0) targetIndex = dots.length - 1;

      dots[targetIndex].click();
    }
  }

  var CbToutArrowsMounter = {
    MOUNT_RETRY_DELAYS_MS: [0, 500, 1200],

    mountAll: function () {
      document.querySelectorAll('.js-tout-section').forEach(function (section) {
        var container = section.querySelector('.container--large');
        var carousel = section.querySelector('.js-tout-carousel.flickity-enabled');
        var dots = section.querySelectorAll('.flickity-page-dots li.dot');

        if (!container || !carousel || !dots.length) return;
        if (section.querySelector('cb-tout-arrows')) return;

        container.appendChild(document.createElement('cb-tout-arrows'));
      });
    },

    start: function () {
      var self = this;
      this.MOUNT_RETRY_DELAYS_MS.forEach(function (delay) {
        setTimeout(function () {
          self.mountAll();
        }, delay);
      });
    }
  };

  /* --------------------------------------------------------------------------
     <cb-countdown-offset>
     ----------------------------------------------------------------------------
     Wraps the countdown timer and keeps it offset by the current header
     height so it is fully visible on large screens.
     ------------------------------------------------------------------------ */

  class CbCountdownOffset extends HTMLElement {
    connectedCallback() {
      if (this.cbOffsetBound) return;
      this.cbOffsetBound = true;

      var timer = this.querySelector('.countdown-timer');
      if (!timer) return;

      timer.classList.remove('timer-hidden');

      var update = this.updateOffset.bind(this);
      update();
      window.addEventListener('resize', update);
      window.addEventListener('load', update);
    }

    updateOffset() {
      var timer = this.querySelector('.countdown-timer');
      var header = document.getElementById('header');
      if (!timer || !header) return;

      timer.style.top = header.offsetHeight + 'px';
    }
  }

  /* --------------------------------------------------------------------------
     Registration and bootstrap
     ------------------------------------------------------------------------ */

  customElements.define('cb-cart-recycle-fee', CbCartRecycleFee);
  customElements.define('cb-pdp-recycle-fee', CbPdpRecycleFee);
  customElements.define('cb-tout-arrows', CbToutArrows);
  customElements.define('cb-countdown-offset', CbCountdownOffset);

  onDocumentReady(function () {
    CbToutArrowsMounter.start();
  });
})();
